import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Building2, LogIn, LogOut} from 'lucide-react';
import {GUESTAPP_NAVIGATION_MODE, resolveHousingName} from '@chekin/core';
import {
  Button,
  ChekinMark,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuValue,
  SidebarProvider,
  cn,
} from '@chekinapp/ui';
import {useReservation} from 'context/reservation';
import {useBrandingTheme} from 'context/brandingTheme';
import {useGuardedNavigate} from 'context/navigationGuard';
import {useSummary} from 'context/summary';
import {useAuth} from 'hooks/auth/useAuth';
import {useFaqEnabled} from 'hooks/useFaqEnabled';
import {AUTH_LOGIN_ROUTE, PATHS} from 'routes/constants';
import {
  NavLinks,
  useNavigationLinks,
  usePath,
  useSideBarLinks,
  usePrivacyPolicyLink,
} from 'hooks';
import {useOnboardingStore} from '@chekin/shared';
import {useLanguageModalStore} from 'components/LanguageModal';
import {goToExternalLink, getInitials} from 'utils/common';

type Props = {
  header?: boolean;
  closeMobileMenu?: () => void;
};

type AppHeaderSettings = {
  navigationTitleConfig: GUESTAPP_NAVIGATION_MODE;
  logo?: string;
};

const NAV_GROUP: Partial<Record<NavLinks, 'before' | 'stay' | 'checkout'>> = {
  [NavLinks.onlineChekin]: 'before',
  [NavLinks.paymentCart]: 'before',
  [NavLinks.chat]: 'stay',
  [NavLinks.virtualKeys]: 'stay',
  [NavLinks.guidebooks]: 'stay',
  [NavLinks.travelGuide]: 'stay',
  [NavLinks.travelEsim]: 'stay',
  [NavLinks.luggageStorage]: 'stay',
  [NavLinks.enhanceYourStay]: 'stay',
  [NavLinks.checkOut]: 'checkout',
};
const GROUP_ORDER = ['before', 'stay', 'checkout'] as const;
const SUPPORT_LINKS = new Set<NavLinks>([
  NavLinks.language,
  NavLinks.faq,
  NavLinks.privacy,
]);

function getHousingImgSrc(appSettingTemplate: AppHeaderSettings, housingLogo?: string) {
  const isLogoConfigured = [
    GUESTAPP_NAVIGATION_MODE.logo,
    GUESTAPP_NAVIGATION_MODE.property_name_and_logo,
  ].includes(appSettingTemplate.navigationTitleConfig);

  const showLogo = !appSettingTemplate?.logo || isLogoConfigured;

  if (!showLogo) {
    return;
  }

  return appSettingTemplate.logo || housingLogo;
}

type NavItem = ReturnType<typeof useSideBarLinks>['navItems'][number];

const BADGE_VARIANT_CLASS: Record<NonNullable<NavItem['extraDataVariant']>, string> = {
  default: '',
  warning: '',
  teal: 'border-transparent bg-[var(--chekin-success-surface)] text-[var(--chekin-success-text)]',
};

function PageSideBar({header, closeMobileMenu}: Props) {
  const {t} = useTranslation();
  const {housing, data: reservation} = useReservation();
  const isFaqEnabled = useFaqEnabled();
  const {data: template} = useBrandingTheme();
  const {isLoggedIn, logout, user} = useAuth();
  const {getFullPath} = usePath();
  const {navItems} = useSideBarLinks();
  const {data} = useSummary();
  const navigate = useNavigate();
  const guardedNavigate = useGuardedNavigate();
  const openLanguageModal = useLanguageModalStore(state => state.open);
  const {resetState} = useOnboardingStore();

  const handleLanguageClick = () => {
    closeMobileMenu?.();
    openLanguageModal();
  };

  const {href: privacyPolicyHref} = usePrivacyPolicyLink({housingId: housing?.id});

  const handleAuthClick = () => {
    if (isLoggedIn) {
      logout();
      resetState();
      closeMobileMenu?.();
      navigate(getFullPath(PATHS.home), {replace: true, state: {isLoggingOut: true}});
    } else {
      navigate(getFullPath(AUTH_LOGIN_ROUTE));
    }
  };

  const handlePrivacyPolicyClick = () => {
    goToExternalLink(privacyPolicyHref);
  };

  const {utilityLinks, authLinks} = useNavigationLinks({
    onLanguageClick: handleLanguageClick,
    onAuthClick: handleAuthClick,
    onPrivacyPolicyClick: handlePrivacyPolicyClick,
    faqsEnable: isFaqEnabled,
  });

  const titleLogo = template?.navigation_title_logo;
  const titleLogoType =
    template?.navigation_title_type || GUESTAPP_NAVIGATION_MODE.property_name;

  const showHeader = header ?? true;
  const name = resolveHousingName(data) ?? '';

  const appSettingTemplate: AppHeaderSettings = {
    navigationTitleConfig: titleLogoType,
    logo: titleLogo ? String(titleLogo) : undefined,
  };

  const housingImg = getHousingImgSrc(appSettingTemplate, housing?.picture?.src);

  const showLogoAndPropertyName = [
    GUESTAPP_NAVIGATION_MODE.property_name,
    GUESTAPP_NAVIGATION_MODE.property_name_and_logo,
  ].includes(titleLogoType);

  const sideBarTitle = showLogoAndPropertyName || !titleLogoType ? name : '';
  const bookingRef = reservation?.booking_reference || '';

  const handleClickOnTitle = useCallback(() => {
    guardedNavigate(getFullPath(PATHS.home));
  }, [getFullPath, guardedNavigate]);

  const topItems = navItems.filter(
    item => item.label === NavLinks.home || !(item.label in NAV_GROUP),
  );

  const supportLinks = utilityLinks.filter(item =>
    SUPPORT_LINKS.has(item.label as NavLinks),
  );
  const sessionLinks = utilityLinks.filter(
    item => !SUPPORT_LINKS.has(item.label as NavLinks),
  );

  const guestEmail = user?.email || '';
  const initials = getInitials(guestEmail);

  const renderUtilityItem = (navItem: (typeof utilityLinks)[number], key: string) => (
    <SidebarMenuItem key={key}>
      <SidebarMenuButton
        size="sm"
        isActive={navItem.selected}
        onClick={navItem.onclick}
        className={cn('min-h-8 py-1.5', navItem.className)}
      >
        {navItem.icon}
        <span>{t(navItem.label)}</span>
        {navItem.value ? <SidebarMenuValue>{navItem.value}</SidebarMenuValue> : null}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  const renderNavItem = (item: NavItem) => (
    <SidebarMenuItem key={item.label}>
      <SidebarMenuButton
        isActive={item.selected}
        onClick={item.onClick}
        className={
          item.selected ? '[background:var(--sidebar-item-active-bg)]' : undefined
        }
      >
        {item.icon}
        <span>{t(item.label)}</span>
      </SidebarMenuButton>
      {item.extraData ? (
        <SidebarMenuBadge
          className={BADGE_VARIANT_CLASS[item.extraDataVariant ?? 'default']}
        >
          {item.extraData}
        </SidebarMenuBadge>
      ) : null}
    </SidebarMenuItem>
  );

  return (
    <SidebarProvider className="min-h-0 w-full">
      <Sidebar
        collapsible="none"
        className="page-sidebar sticky top-[var(--maintenance-banner-height,0px)] h-[var(--app-vh,100vh)] w-full overflow-y-auto [&_[data-sidebar=group-label]]:text-[11px]"
      >
        {showHeader && (
          <SidebarHeader className="gap-0 p-0">
            <button
              type="button"
              onClick={handleClickOnTitle}
              className="flex items-center gap-[11px] px-[22px] pb-[18px] pt-[22px] text-left"
            >
              <span
                className={`flex size-[38px] shrink-0 items-center justify-center overflow-hidden rounded-[11px] [box-shadow:inset_0_0_0_1px_rgba(255,255,255,0.12)] ${
                  housingImg
                    ? '[background:linear-gradient(135deg,color-mix(in_srgb,var(--chekin-color-brand-blue)_50%,transparent),color-mix(in_srgb,var(--chekin-color-brand-light)_32%,transparent))]'
                    : 'bg-white'
                }`}
              >
                {housingImg ? (
                  <img src={housingImg} alt="" className="size-full object-cover" />
                ) : (
                  <ChekinMark className="h-auto w-5 relative top-[1px]" />
                )}
              </span>
              {(sideBarTitle || bookingRef) && (
                <span className="min-w-0">
                  {sideBarTitle && (
                    <span className="line-clamp-4 text-[15px] font-semibold leading-5 text-[var(--sidebar-item-active-text)]">
                      {sideBarTitle}
                    </span>
                  )}
                  {bookingRef && (
                    <span className="mt-px block truncate text-[11px] tabular-nums text-[var(--sidebar-text-muted)]">
                      {bookingRef}
                    </span>
                  )}
                </span>
              )}
            </button>
          </SidebarHeader>
        )}

        <SidebarContent className="pb-5">
          <SidebarGroup>
            <SidebarMenu>{topItems.map(renderNavItem)}</SidebarMenu>
          </SidebarGroup>

          {GROUP_ORDER.map(group => {
            const items = navItems.filter(item => NAV_GROUP[item.label] === group);
            if (items.length === 0) return null;
            return (
              <SidebarGroup key={group}>
                <SidebarGroupLabel>{t(`sidebar_group_${group}`)}</SidebarGroupLabel>
                <SidebarMenu>{items.map(renderNavItem)}</SidebarMenu>
              </SidebarGroup>
            );
          })}

          <div className="mt-auto flex flex-col pt-2">
            {utilityLinks.length > 0 && (
              <SidebarGroup className="py-0">
                <SidebarMenu className="gap-0 [--sidebar-item-gap:10px] [--sidebar-item-icon-opacity:0.75] [--sidebar-item-font-size-sm:13px] [--sidebar-item-icon-size:15px] [--sidebar-item-min-height:32px] [--sidebar-item-text:var(--sidebar-text-muted)]">
                  {[...supportLinks, ...sessionLinks].map((item, index) =>
                    renderUtilityItem(item, `u${index}`),
                  )}
                </SidebarMenu>
              </SidebarGroup>
            )}

            {authLinks.map(item => (
              <Button
                key={item.label}
                variant="ghost"
                onClick={item.onclick}
                className="mx-[var(--sidebar-content-px)] mt-2.5 h-[38px] gap-2 rounded-[11px] border border-solid border-white/15 bg-transparent text-[13px] font-semibold text-[var(--sidebar-item-active-text)] hover:bg-[var(--sidebar-item-hover-bg)] hover:text-[var(--sidebar-item-active-text)] [&_svg]:size-[15px]"
              >
                <LogIn />
                {t(item.label)}
              </Button>
            ))}
          </div>
        </SidebarContent>

        {isLoggedIn && (
          <SidebarFooter className="mx-[14px] flex-row items-center gap-2.5 border-t border-[var(--sidebar-border)] px-2 pb-4 pt-3.5">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[var(--sidebar-badge-bg)] text-[11.5px] font-bold text-[var(--sidebar-badge-text)]">
              {initials || <Building2 size={15} />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] font-semibold leading-4 text-[var(--sidebar-item-active-text)]">
                {t('guest')}
              </span>
              {guestEmail && (
                <span className="block truncate text-[12px] text-[var(--sidebar-text-muted)]">
                  {guestEmail}
                </span>
              )}
            </span>
            <Button
              variant="ghost"
              size="xs"
              onClick={handleAuthClick}
              aria-label={t('logout')}
              className="size-8 min-w-0 shrink-0 rounded-[9px] bg-transparent p-0 text-[var(--sidebar-text-muted)] hover:bg-[var(--sidebar-item-hover-bg)] hover:text-[var(--sidebar-item-active-text)] [&_svg]:size-[16px]"
            >
              <LogOut />
            </Button>
          </SidebarFooter>
        )}
      </Sidebar>
    </SidebarProvider>
  );
}

export {PageSideBar};
